import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import {
  getSocietyRoleDetails,
  assignRolePermissions,
  getPermissionCatalogGrouped,
  clearSocietyRoleDetails,
} from "../../../../redux/actions/SocietyPortal/RolePermissionAction/SocietyRoleAction";
// System role permissions live on a NULL-society_id row shared across every
// society. The society-scoped assign endpoint (rightly) refuses to edit them,
// so we dispatch the super-admin action for system roles instead. The
// controller endpoint (/api/roles/:id/permissions) requires super-admin.
import { setSystemRolePermissions } from "../../../../redux/actions/SocietyController/CtrlSystemRoleAction/CtrlSystemRoleAction";
import { selectedSocietyStorer } from "../../../../utils/webStorer";
import { SOCIETY_PORTAL, SOCIETY_PORTAL_ROLES } from "../../../../Routing/routes";

const flattenCatalog = (catalog = []) =>
  catalog.flatMap((m) =>
    Object.values(m.resources || {}).flat()
  );

const RolePermissions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { roleId } = useParams();

  const societyId = selectedSocietyStorer.getSocietyId();
  const {
    roleDetails,
    detailsLoading,
    assignLoading,
    permissionCatalogGrouped,
    catalogLoading,
  } = useSelector((s) => s.SocietyRoleReducer);
  // Loading flag for the super-admin system-role permission save path.
  // Reducer key may be missing (feature slice not always mounted), so fall
  // back to false rather than blowing up on undefined.
  const sysRoleSaveLoading = useSelector(
    (s) => s.CtrlSystemRoleReducer?.setPermsLoading || false
  );
  const saveLoading = assignLoading || sysRoleSaveLoading;

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [collapsed, setCollapsed] = useState(new Set());
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (societyId && roleId) {
      dispatch(getSocietyRoleDetails({ society_id: societyId, role_id: roleId }));
      dispatch(getPermissionCatalogGrouped({ society_id: societyId }));
    }
    return () => dispatch(clearSocietyRoleDetails());
  }, [dispatch, societyId, roleId]);

  useEffect(() => {
    if (roleDetails?.permissions) {
      setSelectedIds(new Set(roleDetails.permissions.map((p) => p.permission_id)));
    }
  }, [roleDetails]);

  const catalog = useMemo(() => permissionCatalogGrouped || [], [permissionCatalogGrouped]);
  const allCatalogIds = useMemo(
    () => flattenCatalog(catalog).map((p) => p.permission_id),
    [catalog]
  );

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleMany = (ids) => {
    const allSelected = ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleCollapse = (key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(allCatalogIds));
  const clearAll = () => setSelectedIds(new Set());

  const isSystemRole = roleDetails?.is_system_role === 1 || roleDetails?.society_id == null;

  const handleSave = () => {
    const permissionIds = Array.from(selectedIds);
    const onSuccess = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ROLES);

    if (isSystemRole) {
      // Global system role — the super-admin endpoint writes to the shared
      // (society_id=NULL) row and applies to every society. Requires the
      // caller to have super-admin; otherwise the API returns 403 and its
      // saga surfaces the message via toast.
      dispatch(
        setSystemRolePermissions({
          id: roleId,
          permission_ids: permissionIds,
          onSuccess,
        })
      );
    } else {
      dispatch(
        assignRolePermissions({
          society_id: societyId,
          role_id: roleId,
          permission_ids: permissionIds,
          onSuccess,
        })
      );
    }
  };

  const handleCancel = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ROLES);

  const matches = (p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      p.permission_code?.toLowerCase().includes(q) ||
      p.action?.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q)
    );
  };

  if (detailsLoading || catalogLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!roleDetails) {
    return (
      <div className="card radius-12 border-0 shadow-sm">
        <div className="card-body p-40 text-center">
          <Icon icon="mdi:alert-circle-outline" className="text-warning-600 text-4xl mb-12" />
          <h6 className="fw-semibold mb-2">Role Not Found</h6>
          <p className="text-secondary-light text-sm mb-20">
            The role you are trying to edit could not be loaded.
          </p>
          <button onClick={handleCancel} className="btn btn-outline-secondary px-20 py-10 radius-8">
            Back to Roles
          </button>
        </div>
      </div>
    );
  }

  const totalCatalog = allCatalogIds.length;
  const totalSelected = selectedIds.size;

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-12">
        <div>
          <button
            onClick={handleCancel}
            className="btn btn-link text-secondary-light p-0 d-inline-flex align-items-center gap-1 text-sm"
          >
            <Icon icon="mdi:arrow-left" /> Back to Roles
          </button>
          <h6 className="fw-semibold mb-0 mt-1">
            {roleDetails.role_name}{" "}
            <span className="text-secondary-light fw-normal text-sm">
              · {totalSelected}/{totalCatalog} permissions
            </span>
          </h6>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <div className="position-relative">
            <Icon
              icon="mdi:magnify"
              className="position-absolute text-secondary-light"
              style={{ left: 8, top: "50%", transform: "translateY(-50%)" }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search permissions..."
              className="form-control form-control-sm radius-6"
              style={{ paddingLeft: 28, minWidth: 200 }}
            />
          </div>
          <button
            onClick={clearAll}
            className="btn btn-sm btn-outline-secondary radius-6"
            disabled={saveLoading || totalSelected === 0}
          >
            Clear
          </button>
          <button
            onClick={selectAll}
            className="btn btn-sm btn-outline-primary radius-6"
            disabled={saveLoading || totalSelected === totalCatalog}
          >
            Select All
          </button>
          <button
            onClick={handleCancel}
            className="btn btn-sm btn-outline-secondary radius-6"
            disabled={saveLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="btn btn-sm radius-6 text-white d-flex align-items-center gap-1"
            style={{ backgroundColor: "#f59e0b" }}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <Icon icon="mdi:content-save" />
            )}
            Save
          </button>
        </div>
      </div>

      {isSystemRole && (
        <div
          className="alert bg-warning-50 border-warning-200 text-warning-700 d-flex align-items-start gap-2 radius-8 px-16 py-12 mb-16"
          role="alert"
        >
          <Icon icon="mdi:alert" className="text-warning-600 text-xl mt-1 flex-shrink-0" />
          <div className="text-sm">
            <strong>Editing a system role.</strong> Changes here update the global
            defaults for this role and take effect immediately in every society.
            Requires super-admin access to save.
          </div>
        </div>
      )}

      {catalog.length === 0 ? (
        <div className="card radius-8 border-0 shadow-sm">
          <div className="card-body p-24 text-center">
            <Icon icon="mdi:shield-key-outline" className="text-secondary-light text-3xl mb-2" />
            <h6 className="fw-semibold mb-1">Permission Catalog Empty</h6>
            <p className="text-secondary-light text-sm mb-0">
              No permissions are currently defined for this society.
            </p>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {catalog.map((mod) => {
            const moduleIds = Object.values(mod.resources || {})
              .flat()
              .map((p) => p.permission_id);
            const moduleSelected = moduleIds.filter((id) => selectedIds.has(id)).length;
            const moduleAll = moduleSelected === moduleIds.length && moduleIds.length > 0;
            const moduleSome = moduleSelected > 0 && !moduleAll;
            const isCollapsed = collapsed.has(mod.module);

            // Hide entire module if search filters it out
            const visibleResources = Object.entries(mod.resources || {})
              .map(([resource, perms]) => [resource, perms.filter(matches)])
              .filter(([, perms]) => perms.length > 0);
            if (search && visibleResources.length === 0) return null;

            return (
              <div key={mod.module} className="card radius-8 border shadow-sm">
                <div className="card-header py-2 px-12 bg-base border-bottom d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    checked={moduleAll}
                    ref={(el) => {
                      if (el) el.indeterminate = moduleSome;
                    }}
                    onChange={() => toggleMany(moduleIds)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    type="button"
                    onClick={() => toggleCollapse(mod.module)}
                    className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2 flex-grow-1"
                  >
                    <Icon
                      icon={isCollapsed ? "mdi:chevron-right" : "mdi:chevron-down"}
                      className="text-secondary-light"
                    />
                    {mod.metadata?.icon && (
                      <Icon
                        icon={`mdi:${mod.metadata.icon}`}
                        className="text-primary-600"
                      />
                    )}
                    <span className="fw-semibold text-sm">
                      {mod.metadata?.name || mod.module}
                    </span>
                    <span className="badge bg-neutral-100 text-secondary-light fw-medium px-2 py-1 radius-4 text-xs">
                      {moduleSelected}/{moduleIds.length}
                    </span>
                    {mod.metadata?.description && (
                      <span className="text-xs text-secondary-light d-none d-md-inline ms-2">
                        {mod.metadata.description}
                      </span>
                    )}
                  </button>
                </div>

                {!isCollapsed && (
                  <div className="card-body p-12">
                    {(search ? visibleResources : Object.entries(mod.resources || {})).map(
                      ([resource, perms]) => {
                        const resourceIds = perms.map((p) => p.permission_id);
                        const resSelected = resourceIds.filter((id) => selectedIds.has(id)).length;
                        const resAll = resSelected === resourceIds.length && resourceIds.length > 0;
                        const resSome = resSelected > 0 && !resAll;

                        return (
                          <div key={resource} className="mb-2">
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <input
                                type="checkbox"
                                className="form-check-input m-0"
                                checked={resAll}
                                ref={(el) => {
                                  if (el) el.indeterminate = resSome;
                                }}
                                onChange={() => toggleMany(resourceIds)}
                              />
                              <span className="fw-medium text-capitalize text-sm">
                                {resource}
                              </span>
                              <span className="badge bg-neutral-100 text-secondary-light fw-medium px-2 py-1 radius-4 text-xs">
                                {resSelected}/{resourceIds.length}
                              </span>
                            </div>

                            <div className="row gx-2 gy-1 ms-3">
                              {perms.map((p) => {
                                const checked = selectedIds.has(p.permission_id);
                                return (
                                  <div
                                    key={p.permission_id}
                                    className="col-12 col-sm-6 col-lg-4 col-xl-3"
                                  >
                                    <label
                                      className={`d-flex align-items-center gap-2 px-2 py-1 radius-4 cursor-pointer perm-row ${
                                        checked ? "perm-row-active" : ""
                                      }`}
                                      title={
                                        p.description
                                          ? `${p.permission_code} — ${p.description}`
                                          : p.permission_code
                                      }
                                    >
                                      <input
                                        type="checkbox"
                                        className="form-check-input m-0"
                                        checked={checked}
                                        onChange={() => toggle(p.permission_id)}
                                      />
                                      <span className="fw-medium text-sm text-capitalize text-truncate">
                                        {p.action || p.permission_code}
                                      </span>
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        .cursor-pointer { cursor: pointer; }
        .perm-row:hover { background-color: #f3f4f6; }
        .perm-row-active { background-color: #fef3c7; }
        .perm-row-active:hover { background-color: #fde68a; }
      `}</style>
    </>
  );
};

export default RolePermissions;
